import { useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useGroups, useDeleteGroup } from '../../hooks/useGroups';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Skeleton } from '../common/Skeleton';
import { Modal } from '../common/Modal';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { GroupForm } from './GroupForm';
import { Group } from '../../types';

export function GroupList() {
  useDocumentTitle('Groups');
  const { data: groups, isLoading } = useGroups();
  const deleteGroup = useDeleteGroup();
  const { addToast } = useToast();
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function handleDelete() {
    if (!deletingGroup) return;
    deleteGroup.mutate(deletingGroup.id, {
      onSuccess: () => { addToast('success', `Deleted "${deletingGroup.name}"`); setDeletingGroup(null); },
      onError: err => { addToast('error', apiError(err, 'Failed to delete group')); setDeletingGroup(null); },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Groups</h1>
        <Button onClick={() => setShowCreate(true)}>Add group</Button>
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="p-4 space-y-2">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-3 w-1/2" />
            </Card>
          ))}
        </div>
      ) : !groups || groups.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No groups yet.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Groups let you organise the dashboard into sections.
          </p>
          <Button className="mt-4" onClick={() => setShowCreate(true)}>Create your first group</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(g => (
            <Card key={g.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-semibold">{g.name}</h2>
                  {g.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{g.description}</p>}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 tabular-nums">
                    {g.service_ids?.length || 0} service{g.service_ids?.length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="secondary" size="sm" onClick={() => setEditingGroup(g)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeletingGroup(g)}>Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add group">
        <GroupForm onClose={() => setShowCreate(false)} />
      </Modal>

      <Modal open={!!editingGroup} onClose={() => setEditingGroup(null)} title="Edit group">
        {editingGroup && <GroupForm group={editingGroup} onClose={() => setEditingGroup(null)} />}
      </Modal>

      <ConfirmDialog
        open={!!deletingGroup}
        title="Delete group"
        message={`"${deletingGroup?.name}" will be removed. The services in it are not deleted — they return to Ungrouped.`}
        confirmLabel="Delete group"
        loading={deleteGroup.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeletingGroup(null)}
      />
    </div>
  );
}
